import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Command } from 'cmdk'
import {
  Bot,
  CalendarRange,
  ChevronsUpDown,
  Database,
  FileCode2,
  GitBranch,
  GitPullRequest,
  House,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  PenLine,
  Plus,
  Search,
  Settings as SettingsIcon,
  Sparkles,
} from 'lucide-react'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { api } from '../lib/api'
import { initials } from '../lib/format'
import { useCatalog, useMe, useWorkspaceEvents, useWorkspaceState } from '../lib/hooks'
import { THEMES, useTheme } from '../lib/theme'
import type { AskSubject, BusEvent, Me, SavedView, WorkspaceState } from '../lib/types'
import { AgentPanel } from './AgentPanel'
import { AskLayer } from './Ask'
import { ModelDate } from './Periods'
import { Spinner, useToast } from './ui'

/** Asking about things on screen needs the agent; plan.askPreview=1 shows it anyway, to try the interaction. */
const askPreview = () => {
  try {
    return localStorage.getItem('plan.askPreview') === '1'
  } catch {
    return false
  }
}

type WorkspaceCtxValue = { wid: string; state: WorkspaceState | undefined; me: Me | undefined }
const WorkspaceCtx = createContext<WorkspaceCtxValue>({ wid: '', state: undefined, me: undefined })
export const useWorkspace = () => useContext(WorkspaceCtx)

type AgentCtxValue = {
  open: boolean
  setOpen: (v: boolean) => void
  ask: (prompt: string, context?: string, subject?: AskSubject) => void
  pending: { prompt: string; context: string | null; subject: AskSubject | null } | null
  consume: () => void
}
const AgentCtx = createContext<AgentCtxValue>({ open: false, setOpen: () => {}, ask: () => {}, pending: null, consume: () => {} })
export const useAgent = () => useContext(AgentCtx)

/**
 * The navigation, by job: what people come to do, their pages, and (for planners) the tools that
 * build the model. A department owner sees Home, My plan, Views and pages; nothing else.
 */
type NavItem = { href: string; label: string; icon: typeof House; show?: (r: WorkspaceState['role'] | undefined, owns: number) => boolean }
const planner = (r: WorkspaceState['role'] | undefined) => r === 'owner' || r === 'admin' || r === 'planner'
const MAIN: NavItem[] = [
  { href: '/', label: 'Home', icon: House },
  { href: '/plan', label: 'My plan', icon: PenLine, show: (r, owns) => planner(r) || owns > 0 },
  { href: '/cycle', label: 'Cycle', icon: CalendarRange, show: r => planner(r) },
  { href: '/reviews', label: 'Reviews', icon: GitPullRequest, show: r => planner(r) },
  { href: '/views', label: 'Views', icon: LayoutGrid },
]
const BUILD: NavItem[] = [
  { href: '/plans', label: 'Scenarios', icon: GitBranch },
  { href: '/model', label: 'Model', icon: FileCode2 },
  { href: '/data', label: 'Data', icon: Database },
]
const SETTINGS: NavItem = { href: '/settings', label: 'Settings', icon: SettingsIcon }
const NAV: NavItem[] = [...MAIN, { href: '/pages', label: 'Pages', icon: LayoutDashboard }, ...BUILD, SETTINGS]

/** Your pages (and the workspace's shared ones) in the sidebar, a click away wherever you are. */
function NavPages({ wid, location }: { wid: string; location: string }) {
  const q = useQuery({
    queryKey: ['ws', wid, 'pages'],
    queryFn: () => api.get<{ pages: { id: string; title: string; mine: boolean; home: boolean }[] }>(`/w/${wid}/pages`),
  })
  const pages = q.data?.pages ?? []
  const shown = [...pages.filter(p => p.mine), ...pages.filter(p => !p.mine)].slice(0, 8)
  return (
    <nav className="nav-group">
      <div className="nav-label row" style={{ justifyContent: 'space-between' }}>
        <Link href="/pages" className="nav-label-link">
          Pages
        </Link>
        <Link href="/pages?new=1" className="nav-add" aria-label="New page" title="New page">
          <Plus size={12} />
        </Link>
      </div>
      {shown.map(p => (
        <Link
          key={p.id}
          href={`/pages/${p.id}`}
          className={`nav-item sub ${location === `/pages/${p.id}` ? 'active' : ''}`}
          style={{ textDecoration: 'none' }}
        >
          <LayoutDashboard size={14} />
          <span className="ellipsis">{p.title}</span>
          {p.home && <House size={11} className="faint" />}
        </Link>
      ))}
      {!pages.length && (
        <Link href="/pages" className={`nav-item sub faint ${location === '/pages' ? 'active' : ''}`} style={{ textDecoration: 'none' }}>
          <Plus size={14} />
          <span>Make a page</span>
        </Link>
      )}
      {pages.length > 8 && (
        <Link href="/pages" className="nav-item sub faint" style={{ textDecoration: 'none' }}>
          <span style={{ paddingLeft: 22 }}>All {pages.length} pages</span>
        </Link>
      )}
    </nav>
  )
}

function WorkspaceSwitcher({ me, current }: { me: Me | undefined; current: string }) {
  const [open, setOpen] = useState(false)
  const [, navigate] = useLocation()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [])
  const ws = me?.workspaces ?? []
  const cur = ws.find(w => w.id === current)
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="ws-switch" onClick={() => setOpen(!open)}>
        <span className="ws-mark">{initials(cur?.name ?? '?')}</span>
        <span className="name">{cur?.name ?? 'Workspace'}</span>
        <ChevronsUpDown size={14} color="var(--faint)" />
      </button>
      {open && (
        <div className="menu" style={{ top: 48, left: 0, right: 0 }}>
          {ws.map(w => (
            <button
              key={w.id}
              className="menu-item"
              onClick={() => {
                setOpen(false)
                navigate(`~/w/${w.id}`)
              }}
              disabled={w.status !== 'ready'}
            >
              <span className="ws-mark" style={{ width: 20, height: 20, fontSize: 10 }}>
                {initials(w.name)}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
              {w.status !== 'ready' && <span className="pill">{w.status}</span>}
            </button>
          ))}
          <div className="menu-sep" />
          <button className="menu-item" onClick={() => navigate('~/new')}>
            <Plus size={14} /> New workspace from a workbook
          </button>
          <button className="menu-item" onClick={() => navigate('~/workspaces')}>
            <LayoutGrid size={14} /> All workspaces
          </button>
        </div>
      )}
    </div>
  )
}

function Palette({ wid, onClose, onAsk }: { wid: string; onClose: () => void; onAsk: (q: string) => void }) {
  const [, navigate] = useLocation()
  const [q, setQ] = useState('')
  const catalog = useCatalog(wid)
  const [views, setViews] = useState<SavedView[]>([])
  useEffect(() => {
    api
      .get<{ views: SavedView[] }>(`/w/${wid}/views`)
      .then(r => setViews(r.views))
      .catch(() => {})
  }, [wid])
  const go = (href: string) => {
    onClose()
    navigate(href)
  }
  return (
    <div className="cmdk-bg" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <Command label="Command menu" loop>
        <Command.Input autoFocus value={q} onValueChange={setQ} placeholder="Search pages, views, measures — or ask the agent…" />
        <Command.List>
          <Command.Empty>No matches. Press ↵ to ask the agent.</Command.Empty>
          {q.trim().length > 2 && (
            <Command.Group heading="Agent">
              <Command.Item
                value={`ask ${q}`}
                onSelect={() => {
                  onClose()
                  onAsk(q)
                }}
              >
                <Sparkles size={15} /> Ask the agent: “{q}”
              </Command.Item>
            </Command.Group>
          )}
          <Command.Group heading="Go to">
            {NAV.map(n => (
              <Command.Item key={n.href} value={`go ${n.label}`} onSelect={() => go(n.href)}>
                <n.icon size={15} /> {n.label}
              </Command.Item>
            ))}
          </Command.Group>
          {views.length > 0 && (
            <Command.Group heading="Views">
              {views.map(v => (
                <Command.Item key={v.id} value={`view ${v.name}`} onSelect={() => go(`/views/${v.id}`)}>
                  <LayoutGrid size={15} /> {v.name}
                </Command.Item>
              ))}
            </Command.Group>
          )}
          {catalog.data && (
            <Command.Group heading="Measures">
              {catalog.data.measures.map(m => (
                <Command.Item key={m.name} value={`measure ${m.label} ${m.name}`} onSelect={() => go(`/views?measure=${m.name}`)}>
                  <span
                    className={`status-dot`}
                    style={{
                      background: m.kind === 'input' ? 'var(--scripted)' : m.kind === 'actual' ? 'var(--data-ink)' : 'var(--faint)',
                    }}
                  />{' '}
                  {m.label}
                  <span className="faint small mono" style={{ marginLeft: 'auto' }}>
                    {m.name}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command>
    </div>
  )
}

export function Shell({ wid, children }: { wid: string; children: ReactNode }) {
  const me = useMe()
  const state = useWorkspaceState(wid)
  const [location, navigate] = useLocation()
  // on a phone the sidebar is a drawer: it closes as soon as you go somewhere
  const [navOpen, setNavOpen] = useState(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: closing on navigation is the point
  useEffect(() => setNavOpen(false), [location])
  const qc = useQueryClient()
  const toast = useToast()
  const [agentOpen, setAgentOpen] = useState(() => localStorage.getItem('plan.agentOpen') === '1')
  const [pending, setPending] = useState<{ prompt: string; context: string | null; subject: AskSubject | null } | null>(null)
  const [palette, setPalette] = useState(false)
  const [userMenu, setUserMenu] = useState(false)
  const [theme, setTheme] = useTheme()

  useEffect(() => {
    localStorage.setItem('plan.lastWorkspace', wid)
  }, [wid])
  useEffect(() => {
    localStorage.setItem('plan.agentOpen', agentOpen ? '1' : '0')
  }, [agentOpen])
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPalette(p => !p)
      }
      // ⌘J opens and closes the agent; ⌘⇧J (expand) belongs to the panel
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        setAgentOpen(o => !o)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  const onEvent = useCallback(
    (e: BusEvent) => {
      if (e.type === 'tool') toast(`${e.client} · ${e.name.replace(/_/g, ' ')}`, e.summary, <Bot size={15} />)
      if (e.type === 'lake') void qc.invalidateQueries({ queryKey: ['ws', wid, 'lake'] })
      if (e.type === 'tool' && e.name.startsWith('page_')) void qc.invalidateQueries({ queryKey: ['ws', wid, 'pages'] })
    },
    [toast, qc, wid],
  )
  useWorkspaceEvents(wid, onEvent)

  const ask = useCallback((prompt: string, context?: string, subject?: AskSubject) => {
    setPending({ prompt, context: context ?? null, subject: subject ?? null })
    setAgentOpen(true)
  }, [])
  const agentValue = useMemo(
    () => ({ open: agentOpen, setOpen: setAgentOpen, ask, pending, consume: () => setPending(null) }),
    [agentOpen, ask, pending],
  )
  const wsValue = useMemo(() => ({ wid, state: state.data, me: me.data }), [wid, state.data, me.data])

  const logout = async () => {
    await api.post('/auth/logout')
    qc.clear()
    navigate('~/login')
  }

  // a nav item owns its path and anything under it ("/plan" is not "/plans")
  const under = (href: string) => location === href || location.startsWith(`${href}/`)
  const current = NAV.slice(1).find(n => under(n.href))?.label ?? (location.startsWith('/changesets') ? 'Reviews' : 'Home')
  const wsName = state.data?.workspace.name ?? me.data?.workspaces.find(w => w.id === wid)?.name

  return (
    <WorkspaceCtx.Provider value={wsValue}>
      <AgentCtx.Provider value={agentValue}>
        <div className={`app ${agentOpen ? 'with-agent' : ''} ${navOpen ? 'nav-open' : ''}`}>
          {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden />}
          <aside className="sidebar">
            <div className="brand" style={{ paddingBottom: 16 }}>
              <div className="brand-name">
                summation <span>plan</span>
              </div>
            </div>
            <WorkspaceSwitcher me={me.data} current={wid} />
            <nav className="nav-group">
              {MAIN.filter(n => !n.show || n.show(state.data?.role, state.data?.owns?.length ?? 0)).map(n => {
                const active =
                  n.href === '/' ? location === '/' : under(n.href) || (n.href === '/reviews' && location.startsWith('/changesets'))
                return (
                  <Link key={n.href} href={n.href} className={`nav-item ${active ? 'active' : ''}`} style={{ textDecoration: 'none' }}>
                    <n.icon size={15} />
                    <span>{n.label}</span>
                    {n.href === '/reviews' && (state.data?.openChangesets ?? 0) > 0 && (
                      <span className="badge">{state.data?.openChangesets}</span>
                    )}
                  </Link>
                )
              })}
            </nav>
            <NavPages wid={wid} location={location} />
            {planner(state.data?.role) && (
              <nav className="nav-group">
                <div className="nav-label">Build</div>
                {BUILD.map(n => (
                  <Link
                    key={n.href}
                    href={n.href}
                    className={`nav-item ${under(n.href) ? 'active' : ''}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <n.icon size={15} />
                    <span>{n.label}</span>
                  </Link>
                ))}
              </nav>
            )}
            <div className="sidebar-foot" style={{ position: 'relative' }}>
              <button className={`user-chip ${userMenu || under(SETTINGS.href) ? 'active' : ''}`} onClick={() => setUserMenu(!userMenu)}>
                <span className="avatar">{initials(me.data?.user.name ?? '?')}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.data?.user.name}</span>
              </button>
              {userMenu && (
                <div className="menu" style={{ bottom: 44, left: 4, right: 4, minWidth: 0 }}>
                  <div className="small muted" style={{ padding: '6px 10px' }}>
                    {me.data?.user.email}
                  </div>
                  <div className="theme-row" title="Theme">
                    {THEMES.map(t => (
                      <button
                        key={t.key}
                        type="button"
                        aria-pressed={theme === t.key}
                        className={`theme-opt${theme === t.key ? ' on' : ''}`}
                        onClick={() => setTheme(t.key)}
                      >
                        <span className="theme-swatch" style={{ background: t.swatch }} />
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <button
                    className="menu-item"
                    onClick={() => {
                      setUserMenu(false)
                      navigate('/settings')
                    }}
                  >
                    <SettingsIcon size={14} /> Settings
                  </button>
                  <button className="menu-item" onClick={logout}>
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </aside>
          <main className="content">
            <div className="topbar">
              <button type="button" className="icon-btn nav-toggle" onClick={() => setNavOpen(true)} aria-label="Menu">
                <Menu size={16} />
              </button>
              <div className="crumbs">
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{wsName}</span>
                <span className="faint">/</span>
                <b>{current}</b>
                {/* the model's commit matters while you're changing the model, and nowhere else */}
                {state.data && planner(state.data.role) && under('/model') && (
                  <span className="pill mono" style={{ marginLeft: 8 }} title="The model version you're looking at (its latest commit)">
                    main@{state.data.commit}
                  </span>
                )}
              </div>
              <div className="grow" />
              {state.data && <ModelDate closedThrough={state.data.closedThrough} horizonEnd={state.data.horizon.end} />}
              <button
                className="btn sm search-btn"
                onClick={() => setPalette(true)}
                style={{ justifyContent: 'flex-start', color: 'var(--muted)' }}
              >
                <Search size={13} /> <span className="search-label">Search or ask…</span>{' '}
                <span className="kbd-hint" style={{ marginLeft: 'auto' }}>
                  ⌘K
                </span>
              </button>
              <button
                className={`btn sm ${agentOpen ? 'primary' : ''}`}
                onClick={() => setAgentOpen(!agentOpen)}
                title={me.data && !me.data.agent.available ? 'The agent is off on this server (⌘J)' : 'Planning agent (⌘J)'}
              >
                <Sparkles size={13} /> Agent
                {me.data && !me.data.agent.available && <span className="agent-off">off</span>}
              </button>
            </div>
            {state.isLoading ? (
              <div className="empty" style={{ marginTop: 80 }}>
                <Spinner />
              </div>
            ) : state.error ? (
              <div className="page">
                <div className="error-box">{state.error.message}</div>
              </div>
            ) : (
              children
            )}
            {(me.data?.agent.available || askPreview()) && <AskLayer />}
          </main>
          {agentOpen && <AgentPanel wid={wid} onClose={() => setAgentOpen(false)} />}
        </div>
        {palette && <Palette wid={wid} onClose={() => setPalette(false)} onAsk={q => ask(q)} />}
      </AgentCtx.Provider>
    </WorkspaceCtx.Provider>
  )
}
